
import React, { useCallback, useEffect, useRef, type ReactNode, type DragEvent } from 'react'
import {
  ReactFlow,
  Controls,
  MiniMap,
  MarkerType,
  SelectionMode,
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type NodeTypes,
  type EdgeTypes,
  type ReactFlowInstance,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import './react-flow-overrides.css'
import { CANVAS_STYLES } from '@/components/canvas/canvas-styles'
import { MIN_ZOOM, MAX_ZOOM, SNAP_GRID } from '@/components/canvas/canvas-constants'
import { LODController, NodeInternalsExposer, FitViewExposer } from '@/components/canvas/canvas-internals'
import { isolateNodeContentWheel } from '@/components/canvas/canvas-wheel'

/** Stable references to avoid React Flow re-computing on every render */
const DEFAULT_EDGE_OPTIONS = {}
const PRO_OPTIONS = { hideAttribution: true }

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface GenieCanvasProps {
  nodes: Node[]
  edges: Edge[]
  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange
  onConnect: OnConnect
  nodeTypes?: NodeTypes
  edgeTypes?: EdgeTypes
  onNodeClick?: (event: React.MouseEvent, node: Node) => void
  onEdgeClick?: (event: React.MouseEvent, edge: Edge) => void
  onPaneClick?: (event: React.MouseEvent) => void
  onPaneContextMenu?: (event: MouseEvent | React.MouseEvent) => void
  onNodeContextMenu?: (event: React.MouseEvent, node: Node) => void
  onEdgeContextMenu?: (event: React.MouseEvent, edge: Edge) => void
  onDrop?: (event: DragEvent<HTMLDivElement>) => void
  onDragOver?: (event: DragEvent<HTMLDivElement>) => void
  onSelectionChange?: (params: { nodes: Node[]; edges: Edge[] }) => void
  onNodeDragStop?: (event: React.MouseEvent, node: Node, nodes: Node[]) => void
  onInit?: (instance: ReactFlowInstance) => void
  onMoveEnd?: (event: MouseEvent | TouchEvent | null, viewport: { x: number; y: number; zoom: number }) => void
  defaultViewport?: { x: number; y: number; zoom: number }
  /** Show zoom controls panel (default false) */
  showControls?: boolean
  /** Show minimap panel (default false) */
  showMiniMap?: boolean
  /** Hand tool / held space pans instead of selecting nodes. */
  panMode?: boolean
  children?: ReactNode
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function GenieCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  nodeTypes,
  edgeTypes,
  onNodeClick,
  onEdgeClick,
  onPaneClick,
  onPaneContextMenu,
  onNodeContextMenu,
  onEdgeContextMenu,
  onDrop,
  onSelectionChange,
  onNodeDragStop,
  onDragOver,
  onInit,
  onMoveEnd,
  showControls = false,
  showMiniMap = false,
  panMode = false,
  defaultViewport,
  children,
}: GenieCanvasProps) {
  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      onDragOver?.(event)
    },
    [onDragOver],
  )

  // Scrollable node content keeps the wheel only while it can scroll; otherwise the canvas pans.
  const wrapperRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return
    wrapper.addEventListener('wheel', isolateNodeContentWheel, { capture: true })
    return () => wrapper.removeEventListener('wheel', isolateNodeContentWheel, { capture: true })
  }, [])

  return (
    <div ref={wrapperRef} className="h-full w-full" style={{ position: 'relative' }}>
      <style dangerouslySetInnerHTML={{ __html: CANVAS_STYLES }} />

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onPaneContextMenu={onPaneContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeContextMenu={onEdgeContextMenu}
        onDrop={onDrop}
        onSelectionChange={onSelectionChange}
        onNodeDragStop={onNodeDragStop}
        onDragOver={handleDragOver}
        onInit={onInit}
        onMoveEnd={onMoveEnd}
        defaultViewport={defaultViewport || { x: 0, y: 0, zoom: 1 }}
        onlyRenderVisibleElements={true}
        snapToGrid
        snapGrid={SNAP_GRID}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        selectionOnDrag={!panMode}
        nodesDraggable={!panMode}
        nodesConnectable={!panMode}
        elementsSelectable={!panMode}
        selectionMode={SelectionMode.Full}
        panOnDrag={panMode}
        panOnScroll
        zoomOnScroll={false}
        zoomOnPinch
        defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
        proOptions={PRO_OPTIONS}
      >
        {showControls && <Controls />}
        <LODController />
        <NodeInternalsExposer />
        <FitViewExposer />
        {showMiniMap && (
          <MiniMap
            zoomable
            pannable
            nodeStrokeWidth={2}
          />
        )}
        {children}
      </ReactFlow>
    </div>
  )
}

export default GenieCanvas
